import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import AddProduct from "../components/AddProduct";
import UpdateProduct from "../components/EditProduct";
import AuthContext from "../AuthContext";
import { privateAxios } from "../utils/axios";
import toast from "react-hot-toast";

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

function Inventory() {
  const [showProductModal, setShowProductModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateProduct, setUpdateProduct] = useState();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const limitOptions = [5, 10, 20, 50, 100];
  const searchInputRef = useRef(null);
  const authContext = useContext(AuthContext);
  const queryClient = useQueryClient();

  // Debounce the search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500); // 500ms delay

  // Fetch products with React Query
  const {
    data: productsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["products", currentPage, debouncedSearchTerm, limit],
    queryFn: async () => {
      const response = await privateAxios.get(
        `/products/?limit=${limit}&page=${currentPage}&q=${debouncedSearchTerm}`
      );
      if (!response.data.success) {
        throw new Error("Network response was not ok");
      }
      return response.data;
    },
  });

  // Keep focus on search input after data fetch
  useEffect(() => {
    if (
      searchInputRef.current &&
      document.activeElement !== searchInputRef.current
    ) {
      searchInputRef.current.focus();
    }
  }, [productsData]);

  // Modal for Product ADD
  const addProductModalSetting = () => {
    setShowProductModal(!showProductModal);
  };


  // Delete item
  const deleteItem = async (id) => {
    try {
      const response = await privateAxios.delete(`/products/delete/${id}`);
      if (response.data.success) {
        toast.success("Product deleted successfully");
        // Invalidate and refetch products after deletion
        queryClient.invalidateQueries(["products"]);
      } else {
        toast.error(response.data.message || "Failed to delete product");
      }
    } catch (err) {
      console.error("Error deleting product:", err);
      toast.error(err.response?.data?.message || "Error deleting product");
    }
  };

  const handleDeleteClick = async (id) => {
  const confirmed = window.confirm(
    "Are you sure you want to delete this product? This action cannot be undone."
  );

  if (!confirmed) return;

  await deleteItem(id);
};

    const handleEdit = (product) => {
      console.log(product, "edit")
    setSelectedProduct(product);
    setShowUpdateModal(true);
  };

  const editModalSetting = () => {
    setUpdateProduct(false);
    setSelectedProduct(null);
  };

  // Handle Search Term with debounce
  const handleSearchTerm = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  }, []);

  // Handle Page Change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // When limit changes, reset to first page
  useEffect(() => {
    setCurrentPage(1);
  }, [limit]);

  if (error) return <div>Error: {error.message}</div>;

  return (
    <div className="col-span-12 lg:col-span-10 flex justify-center">
      <div className="flex flex-col gap-5 w-11/12">
        {/* --- Overall Inventory Card (new design) --- */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 w-full mt-6 mb-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <span className="font-bold text-xl text-gray-900">
                Overall Inventory
              </span>
              <div className="text-gray-500 text-sm mt-1">
                Overview of your inventory status
              </div>
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-4">
            {/* Total Products */}
            <div className="flex-1 bg-gray-50 rounded-lg flex items-center gap-4 p-5">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100">
                {/* Box/Package icon */}
                <svg
                  className="w-6 h-6 text-blue-500"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4a2 2 0 0 0 1-1.73z"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                  />
                  <polyline
                    points="3.27 6.96 12 12.01 20.73 6.96"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                  />
                  <line
                    x1="12"
                    y1="22.08"
                    x2="12"
                    y2="12"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                  />
                </svg>
              </div>
              <div>
                <div className="text-gray-500 text-sm">Total Products</div>
                <div className="font-bold text-2xl text-gray-900">
                  {productsData?.data?.totalItems || 0}
                </div>
              </div>
            </div>
            {/* Low Stock */}
            <div className="flex-1 bg-gray-50 rounded-lg flex items-center gap-4 p-5">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-yellow-100">
                {/* Warning triangle icon */}
                <svg
                  className="w-6 h-6 text-yellow-500"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 9v4m0 4h.01M10.29 3.86l-8.18 14.14A2 2 0 004 21h16a2 2 0 001.89-2.73l-8.18-14.14a2 2 0 00-3.42 0z"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                  />
                </svg>
              </div>
              <div>
                <div className="text-gray-500 text-sm">Low Stock</div>
                <div className="font-bold text-2xl text-gray-900">
                  {productsData?.data?.lowStock || 0}
                </div>
              </div>
            </div>
            {/* Not in Stock */}
            <div className="flex-1 bg-gray-50 rounded-lg flex items-center gap-4 p-5">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100">
                {/* No entry/circle with line icon */}
                <svg
                  className="w-6 h-6 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                  />
                  <line
                    x1="8"
                    y1="16"
                    x2="16"
                    y2="8"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </div>
              <div>
                <div className="text-gray-500 text-sm">Not in Stock</div>
                <div className="font-bold text-2xl text-gray-900">
                  {productsData?.data?.outOfStock || 0}
                </div>
              </div>
            </div>
          </div>
        </div>

        {showProductModal && (
          <AddProduct
            addProductModalSetting={addProductModalSetting}
            handlePageUpdate={() => {
              queryClient.invalidateQueries(["products"]);
              // toast.success("Products updated successfully");
            }}
          />
        )}

          {/* {showEditModal && selectedSupplier && (
          <EditSupplier
            supplier={selectedSupplier}
            editSupplierModalSetting={editModalSetting}
            handlePageUpdate={() =>
              queryClient.invalidateQueries(["suppliers"])
            }
          />
        )}
         */}

        {showUpdateModal && selectedProduct && (
          <UpdateProduct
            product={selectedProduct}
            editProductModalSetting={editModalSetting}
            handlePageUpdate={() => {
              queryClient.invalidateQueries(["products"]);
            }}
          />
        )}

        {/* Table */}
        <div className="overflow-x-auto rounded-lg border bg-white border-gray-200 mb-10">
          <div className="flex justify-between pt-5 pb-3 px-3 flex-col md:flex-row md:items-center my-3">
            <div className="flex gap-4 justify-center items-center w-full md:w-auto mb-4 md:mb-0">
              <span className="font-bold text-lg">Products</span>
              <div className="relative flex items-center px-2 border-2 rounded-md shadow-sm w-80 bg-white">
                <img
                  alt="search-icon"
                  className="w-5 h-5"
                  src={require("../assets/search-icon.png")}
                />
                <input
                  ref={searchInputRef}
                  className="border-none outline-none focus:outline-none focus:ring-0 focus:border-none text-base pl-2 pr-6 bg-transparent w-full"
                  type="text"
                  placeholder="Search here"
                  value={searchTerm}
                  onChange={handleSearchTerm}
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="absolute right-2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    onClick={() => {
                      setSearchTerm("");
                      searchInputRef.current?.focus();
                    }}
                    tabIndex={-1}
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            <div className="flex gap-4 justify-end w-full md:w-auto">
              <button
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-6 py-2 text-base rounded-lg transition-all duration-200 min-w-[160px]"
                onClick={addProductModalSetting}
              >
                Add Product
              </button>
            </div>
          </div>
          <table className="min-w-full divide-y divide-gray-200 text-sm overflow-hidden shadow-sm border">
            <thead>
              <tr className="bg-gray-200 border-b">
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[8%] text-sm">
                  S.No
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[20%] text-sm">
                  Product Name
                </th>
                {/* <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[15%] text-sm">
                  SKU
                </th> */}
                 <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[15%] text-sm">
                  Unit
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[15%] text-sm">
                  Category
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[12%] text-sm">
                  Stock
                </th>
<th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[18%] text-sm">
                  Action
                </th>

                
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                      <span className="ml-2 text-sm text-gray-500">
                        Loading products...
                      </span>
                    </div>
                  </td>
                </tr>
              ) : productsData?.data?.products?.length > 0 ? (
                productsData.data.products.map((element, index) => (
                  <tr
                    key={element._id}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700 text-sm">
                      {(currentPage - 1) * limit + index + 1}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-900 capitalize text-sm">
                      {element.name}
                    </td>
                    {/* <td className="whitespace-nowrap px-4 py-3 text-gray-700 text-sm">
                      {element.sku}
                    </td> */}
                      <td className="whitespace-nowrap px-4 py-3 text-gray-700 text-sm">
                      {element.unit}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700 capitalize text-sm">
                      {element.category}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700 text-sm">
                      {element.stock}
                    </td>
                   <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(element)}
                        className="bg-blue-500 hover:bg-blue-600 text-white font-medium px-2.5 py-0.5 rounded text-xs transition-colors duration-200"
                      >
                        Edit
                      </button>

                      <button
                        onClick={() => handleDeleteClick(element._id)}
                        className="bg-red-500 hover:bg-red-600 text-white font-medium px-2.5 py-0.5 rounded text-xs transition-colors duration-200"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                  </tr>

                ))
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    className="px-4 py-8 text-center text-sm text-gray-500"
                  >
                    No products found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination - bottom right */}
          <div className="flex justify-end items-center gap-4 py-5 pr-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || isLoading}
                className="px-4 py-2 rounded border text-base disabled:opacity-50 hover:bg-gray-50 transition-all duration-200"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-base">
                Page {currentPage} of {productsData?.data?.totalPages || 1}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={
                  currentPage === (productsData?.data?.totalPages || 1) ||
                  isLoading
                }
                className="px-4 py-2 rounded border text-base disabled:opacity-50 hover:bg-gray-50 transition-all duration-200"
              >
                Next
              </button>
            </div>
            <select
              className="ml-6 px-3 py-2 border rounded text-base min-w-[120px] z-10 bg-white"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
            >
              {limitOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt} / page
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Inventory;
